/**
 * FERP PDF Outline Panel (DRR-079)
 *
 * PDF bookmarks/TOC tree via pdfDocumentProxy.getOutline().
 * Clicking an item navigates to the referenced page.
 */

import { useEffect, useState } from 'react';
import { Stack, Box, Text, UnstyledButton, Loader } from '@mantine/core';
import { IconChevronRight, IconChevronDown } from '@tabler/icons-react';
import { usePdfSuite } from './PdfSuiteContext';
import type { PdfOutlineItem } from './types';

interface OutlinePanelProps {
  pdfDocumentProxy?: unknown;
}

export function OutlinePanel({ pdfDocumentProxy }: OutlinePanelProps) {
  const [outline, setOutline] = useState<PdfOutlineItem[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pdfDocumentProxy) return;
    setLoading(true);

    const proxy = pdfDocumentProxy as { getOutline: () => Promise<PdfOutlineItem[] | null> };
    proxy
      .getOutline()
      .then((items) => setOutline(items ?? []))
      .catch(() => setOutline([]))
      .finally(() => setLoading(false));
  }, [pdfDocumentProxy]);

  if (loading) {
    return (
      <Box p="sm" ta="center">
        <Loader size="sm" />
      </Box>
    );
  }

  if (!outline || outline.length === 0) {
    return (
      <Box p="sm">
        <Text size="xs" c="dimmed">No bookmarks in this PDF</Text>
      </Box>
    );
  }

  return (
    <Stack gap={0} p="xs">
      {outline.map((item, i) => (
        <OutlineItem key={i} item={item} depth={0} pdfDocumentProxy={pdfDocumentProxy} />
      ))}
    </Stack>
  );
}

function OutlineItem({
  item,
  depth,
  pdfDocumentProxy,
}: {
  item: PdfOutlineItem;
  depth: number;
  pdfDocumentProxy?: unknown;
}) {
  const { setCurrentPage } = usePdfSuite();
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = item.items && item.items.length > 0;

  const handleClick = async () => {
    if (!item.dest || !pdfDocumentProxy) return;
    try {
      const proxy = pdfDocumentProxy as {
        getPageIndex: (ref: unknown) => Promise<number>;
        getDestination: (dest: string) => Promise<unknown[] | null>;
      };
      let dest = item.dest;
      if (typeof dest === 'string') {
        const resolved = await proxy.getDestination(dest);
        if (resolved) dest = resolved;
      }
      if (Array.isArray(dest) && dest[0]) {
        const pageIndex = await proxy.getPageIndex(dest[0]);
        setCurrentPage(pageIndex + 1);
      }
    } catch {
      // Destination resolution failed — ignore
    }
  };

  return (
    <>
      <UnstyledButton
        onClick={() => {
          handleClick();
          if (hasChildren) setExpanded(!expanded);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          paddingLeft: depth * 16,
          paddingTop: 4,
          paddingBottom: 4,
          borderRadius: 4,
          width: '100%',
        }}
      >
        {hasChildren ? (
          expanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />
        ) : (
          <Box w={12} />
        )}
        <Text
          size="xs"
          fw={item.bold ? 600 : 400}
          fs={item.italic ? 'italic' : undefined}
          lineClamp={2}
          style={{ flex: 1 }}
        >
          {item.title}
        </Text>
      </UnstyledButton>
      {expanded &&
        hasChildren &&
        item.items.map((child, i) => (
          <OutlineItem
            key={i}
            item={child}
            depth={depth + 1}
            pdfDocumentProxy={pdfDocumentProxy}
          />
        ))}
    </>
  );
}
